import React from "react";
import PropTypes from "prop-types";

import {
  Dialog,
  Grid,
  IconButton,
  makeStyles,
  TextField,
  Typography,
  withStyles,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Switch,
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogActions from "@material-ui/core/DialogActions";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";

import CoinButton from "./CoinButton";
import BoxLoading from "../Components/BoxLoading";

import { searchCoin } from "../apiFunctions";
import { getBalanceAndSymbol } from "../ethereumFunctions";

const styles = (theme) => ({
  dialogContainer: {
    borderRadius: theme.spacing(2),
  },
  titleSection: {
    padding: theme.spacing(2),
  },
  titleText: {
    alignSelf: "center",
  },
  closeButton: {
    padding: 0,
  },
  hr: {
    margin: 0,
    color: theme.palette.grey[400],
  },
  address: {
    borderRadius: "20px",
    paddingBottom: theme.spacing(2),
  },
  searchIcon: {
    color: theme.palette.grey[400],
  },
  coinList: {
    height: "300px",
    overflowY: "auto",
  },
  coinContainer: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  ul: {
    listStyle: "none",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: theme.palette.background.default,
    borderRadius: "16px",
    padding: "2px",
    margin: "0",
    maxWidth: "min-content",
    marginLeft: theme.spacing(1),
  },
  li: {
    cursor: "pointer",
    display: "flex",
    flexFlow: "row nowrap",
    borderRadius: "14px",
    outline: "none",
    textDecoration: "none",
    fontSize: ".8rem",
    fontWeight: "500",
    padding: "9px 13px",
    wordBreak: "break-word",
    overflow: "hidden",
    whiteSpace: "nowrap",
    "&.active": {
      backgroundColor: theme.palette.background.paper,
    },
  },
});

const useStyles = makeStyles(styles);

// This is a modified version of MaterialUI's DialogTitle component, I've added a close button in the top right corner
const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle
      disableTypography
      className={classes.titleSection}
      {...other}
    >
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignContent="center"
      >
        <Typography variant="body1" className={classes.titleText}>
          {children}
        </Typography>

        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            className={classes.closeButton}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </Grid>
    </MuiDialogTitle>
  );
});

export default function CoinDialog({
  onClose,
  open,
  coins,
  account,
  weth,
  chainId,
}) {
  // The CoinDialog component will display a dialog window on top of the page, allowing a user to select a coin
  // from a list (list can be found in 'src/constants/coins.js') or enter an address into a search field. Any entered
  // addresses will first be validated to make sure they exist.
  // When the dialog closes, it will call the `onClose` prop with 1 argument which will either be undefined (if the
  // user closes the dialog without selecting anything), or will be a string containing the address of a coin.

  const classes = useStyles();

  const [loading, setLoading] = React.useState(false);
  const [address, setAddress] = React.useState("");
  const [error, setError] = React.useState("");
  const [searchedCoin, setSerahcedCoin] = React.useState([]);
  const [searchingTimeout, setSearchingTimeout] = React.useState(false);

  const [showLpTokens, setShowLpTokens] = React.useState(true);

  const handleChangeShowLpToken = (event) => {
    setShowLpTokens(event.target.checked);

    search_coin(address, event.target.checked);
  };

  // Resets any fields in the dialog (in case it's opened in the future) and calls the `onClose` prop
  const exit = (address, symbol) => {
    setError("");
    setAddress("");
    onClose(address, symbol);
  };

  // Search Coin
  const search_coin = (value, showLpToken) => {
    setAddress(value);

    clearTimeout(searchingTimeout);

    setSearchingTimeout(
      setTimeout(async () => {
        // set variable
        let params = new URLSearchParams();
        params.set("page", 1);
        params.set("chainId", chainId);
        params.set("coin", value);
        params.set("type", showLpToken ? "lptoken" : "token");

        // start loading
        setLoading(true);

        // request
        await searchCoin(params).then((res) => {
          if (res) {
            // show if coin is on bachednd list
            if (res.length) {
              setSerahcedCoin(res);
            } else {
              setSerahcedCoin([]);
              const addressCheck = /^0x[a-fA-F0-9]{40}$/.test(value);
              if (addressCheck) {
                getBalanceAndSymbol(
                  account,
                  value,
                  weth,
                  coins
                ).then((res) => {
                  if (res) {
                    setSerahcedCoin([{ ...res, address: value }]);
                  }
                });
              }
            }
            //
          } else {
            const addressCheck = /^0x[a-fA-F0-9]{40}$/.test(value);
            if (addressCheck) {
              getBalanceAndSymbol(
                account,
                value,
                weth,
                coins
              ).then((res) => {
                if (res) {
                  setSerahcedCoin([{ ...res, address: value }]);
                }
              });
            }
          }
        });

        // stop loading
        setLoading(false);
      }, 1000)
    );
  };

  // show coin list
  // if user search coin or address show searched coin list
  // if user don't search show default coin list
  const coinList = () => {
    if (address) return searchedCoin;

    return coins;
  };

  const IOSSwitch = withStyles((theme) => ({
    root: {
      width: 42,
      height: 26,
      padding: 0,
      margin: theme.spacing(1),
    },
    switchBase: {
      padding: 1,
      "&$checked": {
        transform: "translateX(16px)",
        color: theme.palette.common.white,
        "& + $track": {
          backgroundColor: theme.palette.primary.main,
          opacity: 1,
          border: "none",
        },
      },
      "&$focusVisible $thumb": {
        color: theme.palette.primary.main,
        border: "6px solid #fff",
      },
    },
    thumb: {
      width: 24,
      height: 24,
    },
    track: {
      borderRadius: 26 / 2,
      border: `1px solid ${theme.palette.grey[400]}`,
      backgroundColor: theme.palette.grey[50],
      opacity: 1,
      transition: theme.transitions.create(["background-color", "border"]),
    },
    checked: {},
    focusVisible: {},
  }))(({ classes, ...props }) => {
    return (
      <Switch
        focusVisibleClassName={classes.focusVisible}
        disableRipple
        classes={{
          root: classes.root,
          switchBase: classes.switchBase,
          thumb: classes.thumb,
          track: classes.track,
          checked: classes.checked,
        }}
        {...props}
      />
    );
  });

  return (
    <Dialog
      open={open}
      onClose={() => exit(undefined, undefined)}
      fullWidth
      maxWidth="xs"
      classes={{ paper: classes.dialogContainer }}
    >
      <DialogTitle onClose={() => exit(undefined, undefined)}>
        Select Coin
      </DialogTitle>

      <div className={classes.coinContainer}>
        <Grid container direction="column" alignContent="center">
          <TextField
            value={address}
            onChange={(e) => search_coin(e.target.value, showLpTokens)}
            variant="outlined"
            placeholder="Search name or paste address"
            error={error !== ""}
            helperText={error}
            fullWidth
            className={classes.address}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className={classes.searchIcon} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </div>

      <div hidden={!address} className={classes.coinContainer}>
        <Typography component="div">
          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item className="pointer">Token</Grid>
            <Grid item>
              <IOSSwitch
                checked={showLpTokens}
                onChange={handleChangeShowLpToken}
                name="checkedB"
              />
            </Grid>
            <Grid item className="pointer">LpToken</Grid>
          </Grid>
        </Typography>
      </div>
      <hr className={classes.hr} />

      <div className="position-relative">
        {loading && <BoxLoading />}
        <Grid container direction="column">
          <Grid item className={classes.coinList}>
            <Grid container direction="column">
              {/* Maps all of the tokens in the constants file to buttons */}
              {coinList().map((coin) => (
                <Grid item key={coin.address} xs={12}>
                  <CoinButton
                    coin={coin}
                    coins={coinList()}
                    account={account}
                    weth={weth}
                    onClick={(address, symbol) => exit(address, symbol)}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </div>
    </Dialog>
  );
}

CoinDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  coins: PropTypes.array.isRequired,
};
